import { useState, useEffect } from "react";
import { scheduleH1Service } from "../../services/scheduleH1.service";
import { productService } from "../../services/product.service";
import { showToast } from "../../lib/toast";
import { Printer } from "lucide-react";
import { formatBusinessDate } from "../../lib/utils";

interface ScheduleH1Entry {
  id: string;
  entryDate: string;
  product: {
    name: string;
    sku: string;
    composition?: string;
    strength?: string;
  };
  patientName: string;
  patientAge?: number;
  patientAddress?: string;
  doctorName: string;
  doctorRegistration?: string;
  prescriptionNumber: string;
  prescriptionDate: string;
  quantityDispensed: number;
  quantityUnit: string;
  openingBalance: number;
  closingBalance: number;
  batchNumber?: string;
  remarks?: string;
  invoice?: {
    invoiceNumber: string;
  };
}

export default function ScheduleH1RegisterPage() {
  const [entries, setEntries] = useState<ScheduleH1Entry[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [startDate, setStartDate] = useState(
    formatBusinessDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)),
  );
  const [endDate, setEndDate] = useState(formatBusinessDate(new Date()));
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    loadEntries();
  }, [selectedProduct, startDate, endDate, page]);

  const loadProducts = async () => {
    try {
      const data = await productService.getProducts({
        scheduleType: "SCHEDULE_H1",
        limit: 1000,
      });
      setProducts(data.data || []);
    } catch (error: any) {
      showToast.error(
        error.response?.data?.message || "Failed to load products",
      );
    }
  };

  const loadEntries = async () => {
    try {
      setLoading(true);
      const data = await scheduleH1Service.getDrugRegister({
        productId: selectedProduct || undefined,
        startDate,
        endDate,
        page,
        limit: 50,
      });
      setEntries(data.data || []);
      setTotalPages(data.totalPages || 1);
    } catch (error: any) {
      showToast.error(
        error.response?.data?.message || "Failed to load register",
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = async () => {
    try {
      const data = await scheduleH1Service.generateReport(startDate, endDate);

      // Convert to CSV
      const headers = [
        "Date",
        "Drug Name",
        "Strength",
        "Patient Name",
        "Age",
        "Doctor Name",
        "Prescription No.",
        "Quantity",
        "Opening Bal.",
        "Closing Bal.",
      ];

      const rows = data.entries.map((entry: any) => [
        new Date(entry.entryDate).toLocaleDateString("en-IN", {
          timeZone: "Asia/Kolkata",
        }),
        entry.product.name,
        entry.product.strength || "-",
        entry.patientName,
        entry.patientAge || "-",
        entry.doctorName,
        entry.prescriptionNumber,
        `${entry.quantityDispensed} ${entry.quantityUnit}`,
        entry.openingBalance,
        entry.closingBalance,
      ]);

      const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `schedule-h1-register-${startDate}-to-${endDate}.csv`;
      a.click();

      showToast.success("Register exported successfully");
    } catch (error: any) {
      showToast.error(error.response?.data?.message || "Failed to export");
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6 print:hidden">
        <h1 className="text-2xl font-bold text-gray-900">
          Schedule H1 Drug Register
        </h1>
        <p className="text-gray-600 mt-1">
          Narcotic and Psychotropic Substances (Form 10)
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6 print:hidden">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Drug
            </label>
            <select
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Schedule H1 Drugs</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name} {product.strength && `(${product.strength})`}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-end gap-2">
            <button
              onClick={handlePrint}
              className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center justify-center gap-2"
            >
              <Printer className="w-4 h-4" />
              Print
            </button>
            <button
              onClick={handleExport}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Register Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            Loading register...
          </div>
        ) : entries.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No entries found for the selected criteria
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Drug Details
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Patient
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Doctor
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Prescription
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Opening
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Closing
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {entries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {new Date(entry.entryDate).toLocaleDateString("en-IN", {
                          timeZone: "Asia/Kolkata",
                        })}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900">
                          {entry.product.name}
                        </div>
                        {entry.product.strength && (
                          <div className="text-gray-500 text-xs">
                            {entry.product.strength}
                          </div>
                        )}
                        {entry.batchNumber && (
                          <div className="text-gray-500 text-xs">
                            Batch: {entry.batchNumber}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900">
                          {entry.patientName}
                        </div>
                        {entry.patientAge && (
                          <div className="text-gray-500 text-xs">
                            Age: {entry.patientAge}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="text-gray-900">{entry.doctorName}</div>
                        {entry.doctorRegistration && (
                          <div className="text-gray-500 text-xs">
                            {entry.doctorRegistration}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="text-gray-900">
                          {entry.prescriptionNumber}
                        </div>
                        <div className="text-gray-500 text-xs">
                          {new Date(entry.prescriptionDate).toLocaleDateString(
                            "en-IN",
                            {
                              timeZone: "Asia/Kolkata",
                            },
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                        {entry.quantityDispensed} {entry.quantityUnit}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">
                        {entry.openingBalance}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                        {entry.closingBalance}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {entry.invoice?.invoiceNumber || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 print:hidden">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print\\:hidden {
            display: none !important;
          }
          table, table * {
            visibility: visible;
          }
          table {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}
